import { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';

type DropListener = (draggedToHtmlId?: string) => void;
type DragEventCallback = () => void;

interface DragAndDropContext {
  dragIds: { [id: string]: DropListener };
  registerDraggable: (id: string, callback: DropListener) => void;
  retrieveListener: (id: string) => DropListener;
  setDropFallback: (action: DragEventCallback) => void;
}

const Context = createContext<DragAndDropContext>(null);

export function DragAndDropProvider({ children }: PropsWithChildren<any>) {
  const [dragIds, setDragIds] = useState<{ [id: string]: DropListener }>({});
  const [dropFallback, setDropFallback] = useState<DragEventCallback>(() => ({}));

  const registerDraggable = useCallback(
    (id: string, callback: () => void) => {
      const registeredIds = Object.assign(dragIds, { [id]: callback });
      setDragIds(registeredIds);
    },
    [dragIds],
  );

  const retrieveListener = useCallback((id: string) => dragIds[id], [dragIds]);

  const updateDropFallback = useCallback((action: DragEventCallback) => setDropFallback(() => action), []);

  const onDragoverCallback = useCallback(event => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const onDropCallback = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      if (dropFallback) {
        dropFallback();
      }
    },
    [dropFallback],
  );

  return (
    <Context.Provider value={{ dragIds, registerDraggable, retrieveListener, setDropFallback: updateDropFallback }}>
      <div onDrop={onDropCallback} onDragOver={onDragoverCallback} style={{ width: '100%', height: '100%' }}>
        {children}
      </div>
    </Context.Provider>
  );
}

export const __useInternalDragAndDrop = (): DragAndDropContext => {
  const contextValues = useContext(Context);
  if (!contextValues) {
    throw Error('__useInternalDragAndDrop must be used in a DragAndDropProvider');
  }

  return contextValues;
};
